import { useState, useEffect, useCallback } from 'react';
import {
  Database, Loader2, Play, Trash2, AlertTriangle, RefreshCcw, Globe, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEnvironment } from '@/contexts/EnvironmentContext';
import { listSeeds, executeSeed, registerSeed, rollbackSeed, Seed } from '@/services/seedsApi';

const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    return response?.data?.error || '';
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return '';
};

const LANG_LABELS: Record<string, string> = {
  fr: 'Francais',
  en: 'English',
  'pt-BR': 'Portugues (BR)',
  pt: 'Portugues',
  de: 'Deutsch',
  es: 'Espanol',
  it: 'Italiano',
  nl: 'Nederlands',
};

export default function SeedsPage() {
  const { current } = useEnvironment();
  const [seeds, setSeeds] = useState<Seed[]>([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ id: string; action: 'execute' | 'rollback' } | null>(null);

  const isProduction = current.name === 'production';

  const fetchSeeds = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSeeds();
      setSeeds(data);
    } catch (err) {
      toast.error('Failed to load seeds: ' + getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSeeds();
  }, [fetchSeeds]);

  const handleExecute = async (id: string) => {
    setExecuting(id);
    try {
      const result = await executeSeed(id);
      toast.success(result.message);
      await fetchSeeds();
    } catch (err) {
      toast.error('Execute failed: ' + getErrorMessage(err));
    } finally {
      setExecuting(null);
      setConfirmAction(null);
    }
  };

  const handleRollback = async (id: string) => {
    setExecuting(id);
    try {
      const result = await rollbackSeed(id);
      toast.success(result.message);
      await fetchSeeds();
    } catch (err) {
      toast.error('Rollback failed: ' + getErrorMessage(err));
    } finally {
      setExecuting(null);
      setConfirmAction(null);
    }
  };

  const handleRegister = async (id: string) => {
    setExecuting(id);
    try {
      const result = await registerSeed(id);
      toast.success(result.message);
      await fetchSeeds();
    } catch (err) {
      toast.error('Register failed: ' + getErrorMessage(err));
    } finally {
      setExecuting(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6 text-slate-600" />
          <h1 className="text-2xl font-bold text-slate-800">Seeds Demo</h1>
        </div>
        <button
          onClick={fetchSeeds}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Rafraichir
        </button>
      </div>

      {/* Production warning */}
      {isProduction && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-semibold text-sm">Environnement de production</p>
            <p className="text-red-600 text-sm">
              Attention : les seeds modifient directement la base de donnees de production.
            </p>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && seeds.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {/* Seeds table */}
      {seeds.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Langue</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Slug</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {seeds.map((seed) => (
                <tr key={seed.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4">
                    <span className="font-medium text-slate-800 text-sm">{seed.name}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-slate-500 text-sm">{seed.description}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                      <Globe className="w-3 h-3" />
                      {LANG_LABELS[seed.lang] || seed.lang}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <code className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{seed.slug}</code>
                  </td>
                  <td className="px-5 py-4">
                    {seed.loaded ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        Charge
                      </span>
                    ) : seed.locked ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        <Lock className="w-3 h-3" />
                        Verrouille ({LANG_LABELS[seed.loadedLang || ''] || seed.loadedLang})
                      </span>
                    ) : seed.unregistered ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        Charge (non identifie)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
                        Non charge
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Unregistered: let user identify which lang is loaded */}
                      {seed.unregistered && (
                        <button
                          onClick={() => handleRegister(seed.id)}
                          disabled={executing !== null}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-lg transition-colors"
                        >
                          {executing === seed.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Globe className="w-3.5 h-3.5" />
                          )}
                          C'est celui-ci
                        </button>
                      )}
                      {/* Not loaded and not locked: allow loading */}
                      {!seed.loaded && !seed.locked && !seed.unregistered && (
                        <button
                          onClick={() => setConfirmAction({ id: seed.id, action: 'execute' })}
                          disabled={executing !== null}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-300 rounded-lg transition-colors"
                        >
                          {executing === seed.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                          Charger
                        </button>
                      )}
                      {/* Loaded: allow rollback */}
                      {seed.loaded && seed.hasUnseed && (
                        <button
                          onClick={() => setConfirmAction({ id: seed.id, action: 'rollback' })}
                          disabled={executing !== null}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 rounded-lg transition-colors"
                        >
                          {executing === seed.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {!loading && seeds.length === 0 && (
        <div className="text-center py-16 text-slate-400">
          <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Aucun fichier seed trouve dans le dossier scripts/</p>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-slate-800 mb-2">
              {confirmAction.action === 'execute' ? 'Charger le seed ?' : 'Supprimer le seed ?'}
            </h3>
            <p className="text-sm text-slate-500 mb-1">
              {seeds.find(s => s.id === confirmAction.id)?.name}
            </p>
            <p className="text-sm text-slate-500 mb-6">
              {confirmAction.action === 'execute'
                ? 'Cette action va creer une entreprise de demonstration avec toutes ses donnees (utilisateurs, equipes, problemes, charts, etc.).'
                : 'Cette action va supprimer definitivement l\'entreprise de demonstration et toutes ses donnees associees.'}
            </p>
            {isProduction && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-xs font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Environnement de production !
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={executing !== null}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => confirmAction.action === 'execute'
                  ? handleExecute(confirmAction.id)
                  : handleRollback(confirmAction.id)}
                disabled={executing !== null}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  confirmAction.action === 'execute'
                    ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-300'
                    : 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                }`}
              >
                {executing !== null && <Loader2 className="w-4 h-4 animate-spin" />}
                {confirmAction.action === 'execute' ? 'Charger' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
